import { useContext, useEffect, useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Checkbox,
  Grid,
  Stack,
  Select,
  Dialog,
  Button,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import { useMutation } from "@apollo/client";
//Import from project
import "../../../Style/dialogstyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import { SelectSubject } from "../../Include/DynamicSelect/DynamicSelect";
import { DialogTitleComp } from "../../../Utils/Component";
import { CREATE_SCHEDULE, UPDATE_SCHEDULE } from "../../../Schema/Schedule";
import SelectTimePicker from "../../../Utils/SelectTimePicker";

export default function ScheduleForm({
  open,
  day,
  mainId,
  classId,
  editData,
  classData,
  setRefetch,
  dialogTitle,
  handleClose,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createSchedule] = useMutation(CREATE_SCHEDULE, {
    onCompleted: ({ createSchedule }) => {
      setLoading(false);

      if (createSchedule?.isSuccess === true) {
        setAlert(true, "success", createSchedule?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createSchedule?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateSchedule] = useMutation(UPDATE_SCHEDULE, {
    onCompleted: ({ updateSchedule }) => {
      setLoading(false);

      if (updateSchedule?.isSuccess === true) {
        setAlert(true, "success", updateSchedule?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateSchedule?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });
  //fromik
  const CheckValidation = Yup.object().shape({
    day: Yup.string().required(t("required")),
    remark: Yup.string(),
    endTime: Yup.string().required(t("required")),
    startTime: Yup.string().required(t("required")),
    subjectId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      day: "Monday",
      remark: "",
      endTime: "",
      startTime: "",
      subjectId: { id: "", title: "" },
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      // console.log("values", values);
      if (dialogTitle === "Update") {
        updateSchedule({
          variables: {
            id: editData?._id,
            input: {
              remark: values?.remark,
              endTime: values?.endTime,
              startTime: values?.startTime,
              subjectId: values?.subjectId?.id || null,
            },
          },
        });
      } else {
        createSchedule({
          variables: {
            input: {
              day: values?.day,
              remark: values?.remark,
              classId: classId || null,
              endTime: values?.endTime,
              startTime: values?.startTime,
              subjectId: values?.subjectId?.id || null,
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (dialogTitle === "Update" && editData) {
      setFieldValue("day", day);
      setFieldValue("subjectId", {
        id: editData?.subjectId?._id,
        title: editData?.subjectId?.khName,
      });
      setFieldValue("endTime", editData?.endTime);
      setFieldValue("startTime", editData?.startTime);
    }

    if (dialogTitle === "Create" && open) {
      resetForm();
    }
  }, [open, editData]);

  const SelectDay = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const translateDay = (day) => {
    const dayOfWeek = {
      Monday: t("thead_monday"),
      Tuesday: t("thead_tuesday"),
      Wednesday: t("thead_wednesday"),
      Thursday: t("thead_thursday"),
      Friday: t("thead_friday"),
      Saturday: t("thead_saturday"),
      Sunday: t("thead_sunday"),
    };
    return dayOfWeek[day] ? dayOfWeek[day] : day;
  };

  return (
    <Dialog open={open} className="dialog-container" maxWidth="sm" fullWidth>
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_schedule")
            : t("modal_title_update_schedule")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <SelectSubject
                  value={values?.subjectId}
                  onChange={(val) => setFieldValue("subjectId", val)}
                />
                {!!errors.subjectId?.id && touched.subjectId?.id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.subjectId?.id}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_start_time")}
                </Typography>
                <SelectTimePicker
                  value={values?.startTime || null}
                  onChange={(val) => setFieldValue("startTime", val || "")}
                />
                {!!errors.startTime && touched.startTime && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.startTime}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_end_time")}
                </Typography>
                <SelectTimePicker
                  value={values?.endTime || null}
                  onChange={(val) => setFieldValue("endTime", val || "")}
                />
                {!!errors.endTime && touched.endTime && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.endTime}
                  </FormHelperText>
                )}
              </Grid>

              {dialogTitle === "Create" && (
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <Typography className={t("class-name-field-title")}>
                    {t("thead_day")}
                  </Typography>

                  <FormControl fullWidth size="small">
                    <Select
                      value={values?.day}
                      onChange={({ target }) =>
                        setFieldValue("day", target.value)
                      }
                    >
                      {SelectDay.map((e, index) => (
                        <MenuItem value={e} key={index}>
                          {translateDay(e)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  rows={2}
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("label_remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                  >
                    {dialogTitle === "Update" ? t("update") : t("add")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
